// Contact-status data contract for the "Contact" tab's status chip
// (ContactStatusIcons.jsx).
//
// CONFIRMED against a real candidate-list response
// (GET /internal/assignments/:id/candidates?status=...): the field is
// `row.notifications`, not the earlier-proposed `row.contact_status`, and
// only email is populated so far — no whatsapp/sms data exists on the
// response at all:
//   row.notifications = {
//     status: {
//       email: {
//         sent, delivered, seen, engaged, bounced, failed, complained: boolean
//       },
//       // whatsapp / sms: not shipped yet — see ContactStatusIcons.jsx's own
//       // comment for why those two chips are dropped rather than mocked.
//     },
//     schedules: [{ schedule: 'YYYY-MM-DD', sent: boolean }, ...],
//   }
//   row.contact_link_send_at = string | null  // when *we* triggered the send
//
// Unlike the single-enum shape this file originally proposed (before the
// real field shipped), email notification state is a bag of independent
// booleans — a webhook can flip `delivered` and `seen` and `engaged` on the
// same row as events land, not a single mutually-exclusive status string.
// `emailStatusFrom` below collapses that bag into the one most-informative
// state to show, same precedence-ladder approach InterviewWhenCell.jsx
// already established for its own multi-flag `interview_details`.
//
// `schedules` (a send-cadence log — which calendar days a contact attempt
// went out) isn't part of this chip; nothing in this tab currently reads
// it.

// One tone system, three renderings of it — the chip itself, and the
// popover's header background + header text (a colored header band instead
// of a small status pill, per design feedback: the header itself carries
// the status, so a redundant pill inside it would just be visual noise).
//
// Every tone shares one `wash` (a low-opacity tint of the same mid-tone
// hue, used as both the chip and header background) rather than Tailwind's
// solid `bg-{color}-100` pastel step — those read noticeably more saturated
// than `slate-100` does, since grey has zero saturation to begin with
// regardless of shade; a flat opacity percentage across all five hues is
// what actually makes info/success/warning/danger read as calm as neutral
// already did, rather than each hue picking its own "light enough" shade by
// eye. `text` (chip icon) stays one step softer than `headerText` (the
// popover heading) — the wash alone carries the "mild" read, so the
// heading doesn't also need dulling to the point of hurting legibility.
const TONES = {
  neutral: {
    wash: 'bg-slate-500/10 dark:bg-slate-400/15',
    text: 'text-slate-500 dark:text-slate-400',
    headerText: 'text-slate-600 dark:text-slate-300',
  },
  info: {
    wash: 'bg-blue-500/10 dark:bg-blue-400/15',
    text: 'text-blue-600 dark:text-blue-400',
    headerText: 'text-blue-700 dark:text-blue-300',
  },
  success: {
    wash: 'bg-green-500/10 dark:bg-green-400/15',
    text: 'text-green-600 dark:text-green-400',
    headerText: 'text-green-700 dark:text-green-300',
  },
  warning: {
    wash: 'bg-amber-500/10 dark:bg-amber-400/15',
    text: 'text-amber-600 dark:text-amber-400',
    headerText: 'text-amber-700 dark:text-amber-300',
  },
  danger: {
    wash: 'bg-red-500/10 dark:bg-red-400/15',
    text: 'text-red-600 dark:text-red-400',
    headerText: 'text-red-700 dark:text-red-300',
  },
};

const toneOf = (tone) => TONES[tone] || TONES.neutral;

export const toneClasses = (tone) =>
  `${toneOf(tone).wash} ${toneOf(tone).text}`;
export const toneHeaderClasses = (tone) => toneOf(tone).wash;
export const toneHeaderTextClasses = (tone) => toneOf(tone).headerText;

// Two axes: seen→engaged is an *engagement* ladder (neutral→blue→green),
// bounced/failed/complained are *failure* states (amber/red) — kept as
// distinct tones rather than collapsed into one "bounced" bucket, since
// `complained` (marked as spam) is a meaningfully worse signal than a mail-
// server `bounced`/`failed`.
export const EMAIL_STATUS_META = {
  sent: {
    label: 'Sent',
    detail: 'Link sent — no response yet.',
    tone: 'neutral',
  },
  delivered: {
    label: 'Delivered',
    detail: 'Reached their inbox — not opened yet.',
    tone: 'info',
  },
  seen: {
    label: 'Seen',
    detail: 'They opened the email.',
    tone: 'info',
  },
  engaged: {
    label: 'Engaged',
    detail: 'They clicked the link.',
    tone: 'success',
  },
  bounced: {
    label: 'Bounced',
    detail: 'Delivery bounced back.',
    tone: 'warning',
  },
  failed: {
    label: 'Failed',
    detail: "Couldn't be delivered.",
    tone: 'danger',
  },
  complained: {
    label: 'Marked as spam',
    detail: 'They reported this email as spam.',
    tone: 'danger',
  },
};

// Precedence, most consequential first — a row can have several flags true
// at once (e.g. delivered + seen + engaged all land as the candidate moves
// through the email), so this picks the single most-informative one to
// show rather than requiring them to be mutually exclusive:
//   complained → failed → bounced → engaged → seen → delivered → sent
// `complained`/`failed`/`bounced` are checked ahead of the engagement
// ladder on the assumption a failure signal is more actionable than a
// stale engagement one, even though in practice they shouldn't co-occur.
// Falls back to `contact_link_send_at` (the row's own "we triggered a
// send" timestamp, independent of these webhook-confirmed flags) when
// every flag is still false — the common case right after a link goes out
// and no webhook has landed yet. Returns null when neither exists, i.e.
// nothing has been attempted at all.
export const emailStatusFrom = (emailFlags, contactLinkSentAt) => {
  if (emailFlags?.complained) return 'complained';
  if (emailFlags?.failed) return 'failed';
  if (emailFlags?.bounced) return 'bounced';
  if (emailFlags?.engaged) return 'engaged';
  if (emailFlags?.seen) return 'seen';
  if (emailFlags?.delivered) return 'delivered';
  if (emailFlags?.sent) return 'sent';
  if (contactLinkSentAt) return 'sent';
  return null;
};
